<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import WorkspaceSettingsLayout from '@/modules/workspace/components/workspace-settings-layout.vue'
import WorkspaceNav from '@/modules/workspace/components/workspace-nav.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { RouterLinkBack, VButton, VButtonInvisible, VToggleButtonField } from '@/modules/shared/components'

const workspaceSettingsStore = useWorkspaceSettingsStore()

const selectedTab = computed(() => 'automated_reminders' as const)

const form = ref({
  send_investor_registration_reminders: true,
  send_past_due_signature_reminders: true,
  send_past_due_invoice_reminders: false,
})

const setForm = () => {
  const settings = workspaceSettingsStore.settings
  if (settings) {
    form.value = {
      send_investor_registration_reminders: settings.send_investor_registration_reminders ?? true,
      send_past_due_signature_reminders: settings.send_past_due_signature_reminders ?? true,
      send_past_due_invoice_reminders: settings.send_past_due_invoice_reminders ?? false,
    }
  }
}

watch(() => workspaceSettingsStore.settings, setForm, { immediate: true })

const submit = async () => {
  await workspaceSettingsStore.updateSettings(form.value)
}

onMounted(() => {
  workspaceSettingsStore.fetch()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <WorkspaceNav />

    <WorkspaceSettingsLayout :selected-tab="selectedTab">
      <form @submit.prevent="submit" class="space-y-6">
        <div class="text-lg font-semibold">Automated Reminders</div>

        <VToggleButtonField
          v-model="form.send_investor_registration_reminders"
          label="Registration reminders"
          description="Send weekly email reminders to investors who haven't completed their registration."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.send_past_due_signature_reminders"
          label="Signature reminders"
          description="Send weekly email reminders for documents awaiting signature."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.send_past_due_invoice_reminders"
          label="Invoice reminders"
          description="Send weekly email reminders for past due capital call invoices."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <div class="pt-4">
          <VButton variant="v-blue" size="lg" type="submit" :loading="workspaceSettingsStore.loading">
            Save Changes
          </VButton>
        </div>
      </form>
    </WorkspaceSettingsLayout>
  </TheLayout>
</template>
